import { MouseEvent, MouseEventHandler, useState } from "react";
import { TooltipRenderProps } from "react-joyride";
import { Colors } from "../../utils/colors";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import ConfirmActionModal from "../Containers/ConfirmPopups/ConfirmActionModal";

const CLOSE_ANIMATION_MS = 180;

// Each mounted tour instance only ever manages a single joyride step (one per
// wizard stage, since only one stage's DOM exists at a time), so the real step
// number/total come through step.data rather than joyride's own index/size.
const OnboardingTourTooltip = (props: TooltipRenderProps) => {
  const { step, primaryProps, skipProps, tooltipProps } = props;
  const { stepNumber, totalSteps } = step.data as {
    stepNumber: number;
    totalSteps?: number;
  };
  const [closing, setClosing] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  const animateThenRun =
    (onClick: MouseEventHandler<HTMLElement>): MouseEventHandler<HTMLElement> =>
    (event) => {
      setClosing(true);
      setTimeout(() => onClick(event), CLOSE_ANIMATION_MS);
    };

  // Skip requires confirmation first - the actual joyride skip only fires once
  // the user confirms, using a synthetic event since there's no real click
  // event at that point (skipProps.onClick just calls event.preventDefault()).
  const confirmedSkipOnPress = () => {
    animateThenRun(skipProps.onClick)({
      preventDefault: () => {},
    } as unknown as MouseEvent<HTMLElement>);
  };

  const buttonStyle = {
    flex: 1,
    borderRadius: 12,
    paddingBlock: 14,
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
  };

  return (
    <>
      <div
        {...tooltipProps}
        className="ColumnNormal"
        style={{
          width: 366,
          backgroundColor: Colors.WHITE,
          paddingBlock: 14,
          borderRadius: 20,
          boxShadow: "-3px 7px 25px 1px #0000004d",
          opacity: closing ? 0 : 1,
          transform: closing ? "scale(0.96)" : "scale(1)",
          transition: `opacity ${CLOSE_ANIMATION_MS}ms ease, transform ${CLOSE_ANIMATION_MS}ms ease`,
        }}
      >
        <div
          className="ColumnNormal"
          style={{ gap: 7, paddingInline: 14, textAlign: "center" }}
        >
          <span className="smallBodySubtext">
            {totalSteps ? `${stepNumber} of ${totalSteps}` : `Step ${stepNumber}`}
          </span>
          <span className="bodyMedium">{step.content}</span>
        </div>
        <HorizontalDivider altMargin={14} />
        <div className="RowNormal" style={{ gap: 14, paddingInline: 14 }}>
          {stepNumber !== totalSteps ? (
            <button
              {...skipProps}
              onClick={() => setShowSkipConfirm(true)}
              style={{
                ...buttonStyle,
                backgroundColor: Colors.WHITE1,
                color: Colors.BLACK,
                border: `0.5px solid ${Colors.GRAY2}`,
              }}
            >
              Skip tour
            </button>
          ) : null}
          <button
            {...primaryProps}
            onClick={animateThenRun(primaryProps.onClick)}
            style={{
              ...buttonStyle,
              backgroundColor: Colors.BLACK,
              color: Colors.WHITE,
            }}
          >
            {stepNumber === totalSteps ? "Finish Tour" : "Continue"}
          </button>
        </div>
      </div>
      <ConfirmActionModal
        heading="Skip and end this tour?"
        subtext="You can revisit this tour by restarting it via the home page."
        confirmButtonText="Skip Tour"
        hideModal={!showSkipConfirm}
        setIsVisible={setShowSkipConfirm}
        confirmOnPress={confirmedSkipOnPress}
      />
    </>
  );
};

export default OnboardingTourTooltip;
